import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TablePagination, useTablePagination } from '@/components/ui/table-pagination';
import { ExportButton, ImportButton } from '@/components/admin/ImportExport';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Loader2, Calculator, Calendar, MoreHorizontal, Edit, Power, PowerOff, Trash2, DollarSign, RefreshCw, Wifi, Package, FileText, Receipt, ChevronDown, User, CreditCard, Server, Edit2 } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { formatPHDate, formatPHDateTime } from '@/lib/utils';

export default function SubscriberManagement() {
  const [subscribers, setSubscribers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedSubscriberHistory, setSelectedSubscriberHistory] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  
  // Pagination states for history dialog
  const [invoicePage, setInvoicePage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const historyPageSize = 10;
  const [assignedEquipment, setAssignedEquipment] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const [activating, setActivating] = useState(false);
  
  // MAC address search states
  const [macSearchResults, setMacSearchResults] = useState([]);
  const [macSearchLoading, setMacSearchLoading] = useState(false);
  const [showMacDropdown, setShowMacDropdown] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  
  // New dialog states
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [chargeDialogOpen, setChargeDialogOpen] = useState(false);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Form states for actions
  const [changePlanForm, setChangePlanForm] = useState({ new_plan_id: '', new_pppoe_profile: '', generate_prorated_bill: true });
  const [deactivateForm, setDeactivateForm] = useState({ disconnection_profile: 'DEACTIVATED', reason: '', generate_final_bill: true });
  const [reactivateForm, setReactivateForm] = useState({ pppoe_profile: '', plan_id: '', generate_prorated_bill: true });
  const [deleteForm, setDeleteForm] = useState({ admin_password: '' });
  const [chargeForm, setChargeForm] = useState({ description: '', amount: '', charge_type: 'Equipment' });
  
  // Invoice adjustment states
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustingInvoice, setAdjustingInvoice] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ new_amount: '', remark: '' });
  const [adjustLoading, setAdjustLoading] = useState(false);
  
  // Edit subscriber states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    barangay: '',
    municipality: '',
    province: '',
    modem_mac: '',
    mikrotik_ids: [],
    grace_period_days: 5,
    billing_day: 30
  });
  const [editProvinces, setEditProvinces] = useState([]);
  const [editMunicipalities, setEditMunicipalities] = useState([]);
  const [editBarangays, setEditBarangays] = useState([]);
  
  // Mikrotik selection states
  const [mikrotikRouters, setMikrotikRouters] = useState([]);
  const [selectedMikrotiks, setSelectedMikrotiks] = useState([]);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    barangay: '',
    municipality: '',
    province: '',
    plan_id: '',
    billing_day: 30,
    grace_period_days: 5,  // Default grace period
    installation_date: new Date().toISOString().split('T')[0],
    modem_mac: '',
    pppoe_username: '',
    pppoe_password: '',
    pppoe_profile: '',
    activate_pppoe: false,
    generate_prorated_bill: true
  });
  const [proratedPreview, setProratedPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    fetchSubscribers();
    fetchPlans();
    fetchProfiles();
    fetchProvinces();
    fetchMikrotikRouters();
  }, []);

  const fetchMikrotikRouters = async () => {
    try {
      const response = await axios.get('/mikrotik/routers');
      const activeRouters = response.data.filter(r => r.is_active !== false);
      setMikrotikRouters(activeRouters);
      // By default, select all active routers
      setSelectedMikrotiks(activeRouters.map(r => r.router_id));
    } catch (error) {
      console.error('Failed to fetch Mikrotik routers');
    }
  };

  // Auto-generate PPPoE username when first/last name changes
  useEffect(() => {
    if (formData.first_name || formData.last_name) {
      const generatedUsername = (formData.first_name + formData.last_name)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ''); // Remove special characters and spaces
      setFormData(prev => ({ ...prev, pppoe_username: generatedUsername }));
    }
  }, [formData.first_name, formData.last_name]);

  // Fetch prorated preview when plan or billing day or installation date changes
  useEffect(() => {
    if (formData.plan_id && formData.billing_day && formData.generate_prorated_bill && formData.installation_date) {
      fetchProratedPreview();
    } else {
      setProratedPreview(null);
    }
  }, [formData.plan_id, formData.billing_day, formData.generate_prorated_bill, formData.installation_date]);

  const fetchProratedPreview = async () => {
    if (!formData.plan_id) return;
    
    setLoadingPreview(true);
    try {
      const response = await axios.post('/billing/preview-prorated', {
        plan_id: formData.plan_id,
        billing_day: formData.billing_day,
        installation_date: formData.installation_date
      });
      setProratedPreview(response.data);
    } catch (error) {
      console.error('Failed to fetch prorated preview');
      setProratedPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/subscribers');
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get('/plans');
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans');
    }
  };

  const fetchProfiles = async () => {
    try {
      // Fetch PPPoE profiles from database instead of Mikrotik
      const response = await axios.get('/pppoe-profiles/active');
      setProfiles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch PPPoE profiles');
    }
  };

  const fetchProvinces = async () => {
    try {
      const response = await axios.get('/addresses/provinces');
      setProvinces(response.data.provinces || []);
    } catch (error) {
      console.error('Failed to fetch provinces');
    }
  };

  const fetchMunicipalities = async (province) => {
    try {
      const response = await axios.get(`/addresses/municipalities/${encodeURIComponent(province)}`);
      setMunicipalities(response.data.municipalities || []);
      setBarangays([]);
    } catch (error) {
      console.error('Failed to fetch municipalities');
    }
  };

  const fetchBarangays = async (province, municipality) => {
    try {
      const response = await axios.get(`/addresses/barangays/${encodeURIComponent(province)}/${encodeURIComponent(municipality)}`);
      setBarangays(response.data.barangays || []);
    } catch (error) {
      console.error('Failed to fetch barangays');
    }
  };

  const handleProvinceChange = (value) => {
    setFormData({ ...formData, province: value, municipality: '', barangay: '' });
    fetchMunicipalities(value);
  };

  const handleMunicipalityChange = (value) => {
    setFormData({ ...formData, municipality: value, barangay: '' });
    fetchBarangays(formData.province, value);
  };

  // MAC address search function
  const searchMacAddress = async (query) => {
    if (!query || query.length < 2) {
      setMacSearchResults([]);
      setShowMacDropdown(false);
      return;
    }
    
    setMacSearchLoading(true);
    try {
      const response = await axios.get(`/inventory/units/search?q=${encodeURIComponent(query)}`);
      // Filter only available units
      const availableUnits = response.data.filter(unit => unit.status === 'available');
      setMacSearchResults(availableUnits);
      setShowMacDropdown(availableUnits.length > 0);
    } catch (error) {
      console.error('Failed to search MAC addresses');
      setMacSearchResults([]);
    } finally {
      setMacSearchLoading(false);
    }
  };

  const handleMacInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFormData({ ...formData, modem_mac: value });
    setSelectedUnit(null); // Clear selection when typing
    searchMacAddress(value);
  };

  const handleSelectUnit = (unit) => {
    setFormData({ ...formData, modem_mac: unit.mac_address || unit.serial_number || '' });
    setSelectedUnit(unit);
    setShowMacDropdown(false);
    setMacSearchResults([]);
  };

  const fetchAssignedEquipment = async (accountNumber) => {
    try {
      const response = await axios.get(`/subscribers/${accountNumber}/equipment`);
      setAssignedEquipment(response.data || []);
    } catch (error) {
      console.error('Failed to fetch equipment');
      setAssignedEquipment([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.plan_id) {
      toast.error('Please select a Subscription Plan');
      return;
    }
    if (!formData.pppoe_username) {
      toast.error('PPPoE Username is required');
      return;
    }
    if (!formData.pppoe_password) {
      toast.error('PPPoE Password is required');
      return;
    }
    if (!formData.pppoe_profile) {
      toast.error('PPPoE Profile is required');
      return;
    }
    
    try {
      const response = await axios.post('/subscribers', {
        ...formData,
        account_number: '',
        is_active: true,
        installation_date: new Date().toISOString(),
        assigned_unit_id: selectedUnit?.unit_id || null,
        mikrotik_ids: selectedMikrotiks.length > 0 ? selectedMikrotiks : null  // null = all active routers
      });
      
      let successMessage = `Subscriber created with account number: ${response.data.account_number}`;
      
      if (response.data.assigned_equipment) {
        successMessage += ` | Equipment assigned: ${response.data.assigned_equipment.mac_address || response.data.assigned_equipment.serial_number}`;
      }
      
      if (response.data.pppoe_created) {
        if (response.data.pppoe_results) {
          const successCount = response.data.pppoe_results.filter(r => r.success).length;
          const totalCount = response.data.pppoe_results.length;
          successMessage += ` | PPPoE created on ${successCount}/${totalCount} Mikrotik(s) ✓`;
        } else {
          successMessage += ' | PPPoE account created in Mikrotik ✓';
        }
      } else if (response.data.pppoe_error) {
        successMessage += ` | PPPoE creation failed: ${response.data.pppoe_error}`;
      }
      
      if (response.data.prorated_invoice) {
        const invoice = response.data.prorated_invoice;
        successMessage += ` | Prorated invoice: ₱${invoice.amount} (${invoice.days_covered} days)`;
      } else if (response.data.billing_note) {
        successMessage += ` | ${response.data.billing_note}`;
      }
      
      toast.success(successMessage);
      setDialogOpen(false);
      setFormData({ 
        first_name: '', 
        last_name: '', 
        email: '', 
        phone: '', 
        street: '',
        barangay: '',
        municipality: '',
        province: '',
        plan_id: '', 
        billing_day: 30,
        grace_period_days: 5,
        installation_date: new Date().toISOString().split('T')[0],
        modem_mac: '',
        pppoe_username: '',
        pppoe_password: '',
        pppoe_profile: '',
        activate_pppoe: false,
        generate_prorated_bill: true
      });
      // Reset Mikrotik selection to all active routers
      setSelectedMikrotiks(mikrotikRouters.map(r => r.router_id));
      setProratedPreview(null);
      setSelectedUnit(null);
      setMunicipalities([]);
      setBarangays([]);
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to create subscriber');
    }
  };

  // Pagination state for subscribers
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filteredSubscribers = useMemo(() => {
    return subscribers.filter((sub) =>
      sub.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.account_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [subscribers, searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredSubscribers.length / pageSize));
  const paginatedSubscribers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSubscribers.slice(start, start + pageSize);
  }, [filteredSubscribers, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSelectSubscriber = (accountNumber) => {
    setSelectedSubscribers(prev => 
      prev.includes(accountNumber) 
        ? prev.filter(id => id !== accountNumber)
        : [...prev, accountNumber]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedSubscribers(filteredSubscribers.map(sub => sub.account_number));
    } else {
      setSelectedSubscribers([]);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedSubscribers.length === 0) {
      toast.error('No subscribers selected');
      return;
    }

    setActivating(true);
    try {
      const response = await axios.post('/subscribers/bulk-activate-pppoe', selectedSubscribers);
      const { results } = response.data;
      
      let message = `✓ Activated ${results.success.length} accounts`;
      if (results.failed.length > 0) {
        message += ` | ${results.failed.length} failed`;
      }
      if (results.skipped.length > 0) {
        message += ` | ${results.skipped.length} skipped`;
      }
      
      toast.success(message);
      setSelectedSubscribers([]);
      fetchSubscribers();
    } catch (error) {
      toast.error('Bulk activation failed');
    } finally {
      setActivating(false);
    }
  };

  const handleActivateSingle = async (accountNumber) => {
    try {
      const response = await axios.post(`/subscribers/${accountNumber}/activate-pppoe`);
      if (response.data.already_exists) {
        toast.success('PPPoE account already exists in Mikrotik. Status updated to Active.', { duration: 5000 });
      } else {
        toast.success('PPPoE account created and activated in Mikrotik');
      }
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate PPPoE');
    }
  };

  const handleViewHistory = async (subscriber) => {
    try {
      setSelectedSubscriberHistory(subscriber);
      const [paymentsRes, invoicesRes, equipmentRes] = await Promise.all([
        axios.get(`/payments/subscriber/${subscriber.account_number}`),
        axios.get(`/invoices/subscriber/${subscriber.account_number}`),
        axios.get(`/subscribers/${subscriber.account_number}/equipment`)
      ]);
      
      // Sort invoices by date (latest first)
      const sortedInvoices = [...invoicesRes.data].sort((a, b) => {
        const dateA = new Date(a.created_at || a.due_date);
        const dateB = new Date(b.created_at || b.due_date);
        return dateB - dateA;
      });
      
      // Sort payments by date (latest first)
      const sortedPayments = [...paymentsRes.data].sort((a, b) => {
        const dateA = new Date(a.payment_date || a.created_at);
        const dateB = new Date(b.payment_date || b.created_at);
        return dateB - dateA;
      });
      
      setPaymentHistory(sortedPayments);
      setInvoiceHistory(sortedInvoices);
      setAssignedEquipment(equipmentRes.data || []);
      
      // Reset pagination
      setInvoicePage(1);
      setPaymentPage(1);
      
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch payment history');
    }
  };

  const handlePrintSOA = async (subscriber) => {
    try {
      toast.info('Generating SOA...');
      const response = await axios.get(`/soa/${subscriber.account_number}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SOA_${subscriber.account_number}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('SOA downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate SOA');
      console.error('SOA generation error:', error);
    }
  };

  // Edit subscriber handlers
  const openEditDialog = async (subscriber) => {
    setSelectedSubscriber(subscriber);
    setEditForm({
      first_name: subscriber.first_name || '',
      last_name: subscriber.last_name || '',
      email: subscriber.email || '',
      phone: subscriber.phone || '',
      street: subscriber.street || '',
      barangay: subscriber.barangay || '',
      municipality: subscriber.municipality || '',
      province: subscriber.province || '',
      modem_mac: subscriber.modem_mac || '',
      mikrotik_ids: subscriber.mikrotik_ids || [],
      grace_period_days: subscriber.grace_period_days ?? 5,
      billing_day: subscriber.billing_day || 30
    });
    
    // Load provinces
    try {
      const provRes = await axios.get('/addresses/provinces');
      setEditProvinces(provRes.data.provinces || []);
      
      // If province exists, load municipalities
      if (subscriber.province) {
        const muniRes = await axios.get(`/addresses/municipalities/${subscriber.province}`);
        setEditMunicipalities(muniRes.data.municipalities || []);
        
        // If municipality exists, load barangays
        if (subscriber.municipality) {
          const brgyRes = await axios.get(`/addresses/barangays/${subscriber.province}/${subscriber.municipality}`);
          setEditBarangays(brgyRes.data.barangays || []);
        }
      }
    } catch (error) {
      console.error('Failed to load address data');
    }
    
    setEditDialogOpen(true);
  };

  const handleEditProvinceChange = async (province) => {
    setEditForm({ ...editForm, province, municipality: '', barangay: '' });
    setEditBarangays([]);
    try {
      const res = await axios.get(`/addresses/municipalities/${province}`);
      setEditMunicipalities(res.data.municipalities || []);
    } catch (error) {
      setEditMunicipalities([]);
    }
  };

  const handleEditMunicipalityChange = async (municipality) => {
    setEditForm({ ...editForm, municipality, barangay: '' });
    try {
      const res = await axios.get(`/addresses/barangays/${editForm.province}/${municipality}`);
      setEditBarangays(res.data.barangays || []);
    } catch (error) {
      setEditBarangays([]);
    }
  };

  const handleUpdateSubscriber = async () => {
    if (!selectedSubscriber) return;
    setActionLoading(true);
    try {
      const response = await axios.put(`/subscribers/${selectedSubscriber.account_number}/details`, editForm);
      toast.success(response.data.message || 'Subscriber updated successfully');
      setEditDialogOpen(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  // Action handlers
  const openChangePlanDialog = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setChangePlanForm({
      new_plan_id: subscriber.plan_id || '',
      new_pppoe_profile: subscriber.pppoe_profile || '',
      generate_prorated_bill: true
    });
    setChangePlanDialogOpen(true);
  };

  const handleChangePlan = async () => {
    if (!selectedSubscriber) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`/subscribers/${selectedSubscriber.account_number}/change-plan`, changePlanForm);
      toast.success(response.data.message);
      if (response.data.prorated_invoice) {
        toast.info(`Prorated invoice: ₱${response.data.prorated_invoice.amount} (${response.data.prorated_invoice.type})`);
      }
      setChangePlanDialogOpen(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeactivateDialog = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setDeactivateForm({
      disconnection_profile: 'DEACTIVATED',
      reason: '',
      generate_final_bill: true
    });
    setDeactivateDialogOpen(true);
  };

  const handleDeactivate = async () => {
    if (!selectedSubscriber) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`/subscribers/${selectedSubscriber.account_number}/deactivate`, deactivateForm);
      toast.success(response.data.message);
      if (response.data.mikrotik_profile_changed) {
        toast.info(`Mikrotik profile changed to: ${response.data.mikrotik_profile_changed}`, { duration: 4000 });
      }
      if (response.data.active_session_disconnected) {
        toast.info('Active session disconnected - new profile now in effect', { duration: 4000 });
      }
      if (response.data.final_invoice) {
        toast.info(`Final bill: ₱${response.data.final_invoice.amount} (${response.data.final_invoice.days_charged} days)`);
      }
      setDeactivateDialogOpen(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  const openReactivateDialog = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setReactivateForm({
      pppoe_profile: subscriber.previous_pppoe_profile || '',
      plan_id: subscriber.plan_id || '',
      generate_prorated_bill: false  // Default to false - prorated bill generated on billing day
    });
    setReactivateDialogOpen(true);
  };

  const handleReactivate = async () => {
    if (!selectedSubscriber) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`/subscribers/${selectedSubscriber.account_number}/reactivate`, reactivateForm);
      toast.success(response.data.message);
      if (response.data.mikrotik_profile_changed) {
        toast.info(`Mikrotik profile changed to: ${response.data.mikrotik_profile_changed}`, { duration: 4000 });
      }
      if (response.data.active_session_disconnected) {
        toast.info('Active session disconnected - new profile now in effect', { duration: 4000 });
      }
      if (response.data.prorated_invoice) {
        toast.info(`Prorated bill: ₱${response.data.prorated_invoice.amount} (${response.data.prorated_invoice.days_covered} days)`);
      }
      setReactivateDialogOpen(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setDeleteForm({ admin_password: '' });
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedSubscriber || !deleteForm.admin_password) return;
    setActionLoading(true);
    try {
      const response = await axios.delete(`/subscribers/${selectedSubscriber.account_number}`, { data: deleteForm });
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      fetchSubscribers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete subscriber');
    } finally {
      setActionLoading(false);
    }
  };

  const openChargeDialog = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setChargeForm({ description: '', amount: '', charge_type: 'Equipment' });
    setChargeDialogOpen(true);
  };

  const handleAddCharge = async () => {
    if (!selectedSubscriber || !chargeForm.description || !chargeForm.amount) return;
    setActionLoading(true);
    try {
      const response = await axios.post(`/subscribers/${selectedSubscriber.account_number}/charges`, chargeForm);
      toast.success(`Charge added: ₱${chargeForm.amount} - ${chargeForm.description}`);
      setChargeDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add charge');
    } finally {
      setActionLoading(false);
    }
  };

  // Invoice adjustment functions
  const openAdjustDialog = (invoice) => {
    setAdjustingInvoice(invoice);
    setAdjustForm({ new_amount: invoice.amount?.toString() || '', remark: '' });
    setAdjustDialogOpen(true);
  };

  const handleAdjustInvoice = async () => {
    if (!adjustingInvoice || !adjustForm.new_amount || !adjustForm.remark.trim()) {
      toast.error('Please enter new amount and remark');
      return;
    }
    
    const newAmount = parseFloat(adjustForm.new_amount);
    if (isNaN(newAmount) || newAmount < 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setAdjustLoading(true);
    try {
      const response = await axios.put(`/invoices/${adjustingInvoice.invoice_number}/adjust`, {
        new_amount: newAmount,
        remark: adjustForm.remark.trim()
      });
      
      const adjustment = response.data.adjustment;
      toast.success(`Invoice adjusted: ₱${adjustment.old_amount.toLocaleString()} → ₱${adjustment.new_amount.toLocaleString()}`);
      setAdjustDialogOpen(false);
      
      // Refresh invoice history
      if (selectedSubscriberHistory) {
        const invoicesRes = await axios.get(`/invoices/subscriber/${selectedSubscriberHistory.account_number}`);
        // Sort invoices by date (latest first)
        const sortedInvoices = (invoicesRes.data || []).sort((a, b) => 
          new Date(b.created_at || b.due_date) - new Date(a.created_at || a.due_date)
        );
        setInvoiceHistory(sortedInvoices);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust invoice');
    } finally {
      setAdjustLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section - Mobile Responsive */}
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold" data-testid="subscriber-management-title">Subscriber Management</h2>
          <p className="text-muted-foreground mt-1 text-sm">Manage subscriber accounts</p>
        </div>
        {/* Action Buttons - Stack on mobile */}
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton 
            endpoint="/export/subscribers" 
            filename={`subscribers_${new Date().toISOString().split('T')[0]}.csv`}
            label="Export"
          />
          <ImportButton 
            endpoint="/import/subscribers" 
            templateType="subscribers"
            onSuccess={fetchSubscribers}
            label="Import"
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-subscriber-button" size="sm" className="sm:size-default">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Subscriber</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Register New Subscriber</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input 
                    value={formData.first_name} 
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value.toUpperCase() })} 
                    style={{ textTransform: 'uppercase' }}
                    required 
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input 
                    value={formData.last_name} 
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value.toUpperCase() })} 
                    style={{ textTransform: 'uppercase' }}
                    required 
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              
              {/* Address Section */}
              <div className="col-span-2 pt-4 border-t">
                <h4 className="font-medium mb-3">Address Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Province</Label>
                    <Select value={formData.province} onValueChange={handleProvinceChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select province" />
                      </SelectTrigger>
                      <SelectContent>
                        {provinces.map((prov) => (
                          <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Municipality/City</Label>
                    <Select 
                      value={formData.municipality} 
                      onValueChange={handleMunicipalityChange}
                      disabled={!formData.province}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select municipality" />
                      </SelectTrigger>
                      <SelectContent>
                        {municipalities.map((muni) => (
                          <SelectItem key={muni} value={muni}>{muni}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Barangay</Label>
                    <Select 
                      value={formData.barangay} 
                      onValueChange={(value) => setFormData({ ...formData, barangay: value })}
                      disabled={!formData.municipality}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select barangay" />
                      </SelectTrigger>
                      <SelectContent>
                        {barangays.map((brgy) => (
                          <SelectItem key={brgy} value={brgy}>{brgy}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Street/House No.</Label>
                    <Input 
                      value={formData.street} 
                      onChange={(e) => setFormData({ ...formData, street: e.target.value })} 
                      placeholder="e.g., 123 Main Street"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label>Subscription Plan <span className="text-red-500">*</span></Label>
                <Select value={formData.plan_id} onValueChange={(value) => setFormData({ ...formData, plan_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.name} value={plan.name}>{plan.name} - ₱{plan.price}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Installation Date</Label>
                  <Input 
                    type="date" 
                    value={formData.installation_date} 
                    onChange={(e) => setFormData({ ...formData, installation_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Billing Day (Day of Month)</Label>
                  <Select 
                    value={formData.billing_day.toString()} 
                    onValueChange={(value) => setFormData({ ...formData, billing_day: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select billing day" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of Month
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Grace Period (Days)</Label>
                  <Select 
                    value={formData.grace_period_days?.toString() || '5'} 
                    onValueChange={(value) => setFormData({ ...formData, grace_period_days: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select grace period" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {Array.from({ length: 31 }, (_, i) => i).map((day) => (
                        <SelectItem key={day} value={day.toString()}>
                          {day === 0 ? 'No grace period' : `${day} day${day > 1 ? 's' : ''}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Days after due date before auto-deactivation
                  </p>
                </div>
              </div>
              
              {/* Prorated Billing Section */}
              <div className="col-span-2 mt-2">
                <div className="flex items-center space-x-2 mb-3">
                  <Checkbox 
                    id="generate_prorated_bill" 
                    checked={formData.generate_prorated_bill}
                    onCheckedChange={(checked) => setFormData({ ...formData, generate_prorated_bill: checked })}
                  />
                  <Label htmlFor="generate_prorated_bill" className="text-sm cursor-pointer">
                    Generate prorated bill (charge from installation date until billing date)
                  </Label>
                </div>
                
                {formData.generate_prorated_bill && formData.plan_id && (
                  <div className="bg-muted/50 rounded-lg p-3 border">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">Prorated Bill Preview</span>
                    </div>
                    {loadingPreview ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Calculating...
                      </div>
                    ) : proratedPreview ? (
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Plan:</span>
                          <span className="font-medium">{proratedPreview.plan_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Monthly Rate:</span>
                          <span>₱{proratedPreview.monthly_rate?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Period:</span>
                          <span>{proratedPreview.start_date} to {proratedPreview.end_date}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Days Covered:</span>
                          <span>{proratedPreview.days_covered} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Daily Rate:</span>
                          <span>₱{proratedPreview.daily_rate}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t mt-2">
                          <span className="font-medium">Prorated Amount:</span>
                          <span className="font-bold text-primary text-lg">₱{proratedPreview.prorated_amount?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Calendar className="h-3 w-3" />
                          Due: {proratedPreview.due_date}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Select a plan to see prorated calculation</p>
                    )}
                  </div>
                )}
                
                {!formData.generate_prorated_bill && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <strong>No prorated bill:</strong> First invoice will be generated on the {formData.billing_day}{formData.billing_day === 1 ? 'st' : formData.billing_day === 2 ? 'nd' : formData.billing_day === 3 ? 'rd' : 'th'} of the month during the regular billing cycle.
                    </p>
                  </div>
                )}
              </div>

              <div className="relative">
                <Label>Modem MAC Address / Equipment <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                <div className="relative">
                  <Input 
                    value={formData.modem_mac} 
                    onChange={handleMacInputChange}
                    onFocus={() => formData.modem_mac.length >= 2 && macSearchResults.length > 0 && setShowMacDropdown(true)}
                    onBlur={() => setTimeout(() => setShowMacDropdown(false), 200)}
                    placeholder="Type MAC address to search inventory..."
                    className={selectedUnit ? "border-green-500 bg-green-50" : ""}
                  />
                  {macSearchLoading && (
                    <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {showMacDropdown && macSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border rounded-md shadow-lg max-h-60 overflow-auto">
                    {macSearchResults.map((unit) => (
                      <div
                        key={unit.unit_id}
                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer border-b last:border-b-0"
                        onMouseDown={() => handleSelectUnit(unit)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-mono text-sm font-medium">{unit.mac_address || unit.serial_number}</p>
                            <p className="text-xs text-muted-foreground">{unit.item_name || unit.item_code}</p>
                          </div>
                          <Badge variant="outline" className="text-green-600 border-green-300">Available</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Selected Unit Info */}
                {selectedUnit && (
                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-950 border border-green-200 rounded-md flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wifi className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800 dark:text-green-200">
                          {selectedUnit.item_name || selectedUnit.item_code}
                        </p>
                        <p className="text-xs text-green-600">
                          {selectedUnit.mac_address && `MAC: ${selectedUnit.mac_address}`}
                          {selectedUnit.mac_address && selectedUnit.serial_number && ' | '}
                          {selectedUnit.serial_number && `S/N: ${selectedUnit.serial_number}`}
                        </p>
                      </div>
                    </div>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        setSelectedUnit(null);
                        setFormData({ ...formData, modem_mac: '' });
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground mt-1">
                  Search by MAC address or serial number to assign equipment from inventory
                </p>
              </div>
              
              {/* PPPoE Account Section */}
              <div className="pt-4 border-t">
                <h4 className="font-medium mb-3 text-primary">PPPoE Account Configuration <span className="text-red-500">*</span></h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>PPPoE Username <span className="text-red-500">*</span></Label>
                    <Input 
                      value={formData.pppoe_username} 
                      onChange={(e) => setFormData({ ...formData, pppoe_username: e.target.value })} 
                      placeholder="username@isp"
                    />
                  </div>
                  <div>
                    <Label>PPPoE Password <span className="text-red-500">*</span></Label>
                    <Input 
                      type="password"
                      value={formData.pppoe_password} 
                      onChange={(e) => setFormData({ ...formData, pppoe_password: e.target.value })} 
                      placeholder="Enter password"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>PPPoE Profile <span className="text-red-500">*</span></Label>
                    {profiles.length > 0 ? (
                      <Select value={formData.pppoe_profile} onValueChange={(value) => setFormData({ ...formData, pppoe_profile: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select PPPoE profile" />
                        </SelectTrigger>
                        <SelectContent>
                          {profiles.map((profile) => (
                            <SelectItem key={profile} value={profile}>{profile}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="space-y-2">
                        <Input 
                          value={formData.pppoe_profile} 
                          onChange={(e) => setFormData({ ...formData, pppoe_profile: e.target.value })} 
                          placeholder="e.g., 10Mbps, 20Mbps"
                        />
                        <p className="text-xs text-muted-foreground">
                          Add PPPoE Profiles in Settings to see dropdown options
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Mikrotik Router Selection */}
                {mikrotikRouters.length > 0 && (
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        Target Mikrotik Router(s)
                      </Label>
                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedMikrotiks(mikrotikRouters.map(r => r.router_id))}
                          className="text-xs h-7"
                        >
                          Select All
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedMikrotiks([])}
                          className="text-xs h-7"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {mikrotikRouters.map((router) => (
                        <div 
                          key={router.router_id}
                          className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer transition-colors ${
                            selectedMikrotiks.includes(router.router_id) 
                              ? 'bg-primary/10 border-primary' 
                              : 'bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                          }`}
                          onClick={() => {
                            setSelectedMikrotiks(prev => 
                              prev.includes(router.router_id)
                                ? prev.filter(id => id !== router.router_id)
                                : [...prev, router.router_id]
                            );
                          }}
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                            selectedMikrotiks.includes(router.router_id)
                              ? 'bg-primary border-primary text-white'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {selectedMikrotiks.includes(router.router_id) && (
                              <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                                <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{router.name}</p>
                            <p className="text-xs text-muted-foreground">{router.ip_address}:{router.port || 8728}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {selectedMikrotiks.length === 0 
                        ? 'PPPoE account will be created on ALL active routers'
                        : `PPPoE account will be created on ${selectedMikrotiks.length} router(s)`
                      }
                    </p>
                  </div>
                )}
                
                <div className="mt-4 flex items-center space-x-2 p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                  <input
                    type="checkbox"
                    id="activate_pppoe"
                    checked={formData.activate_pppoe}
                    onChange={(e) => setFormData({ ...formData, activate_pppoe: e.target.checked })}
                    className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                  />
                  <label htmlFor="activate_pppoe" className="text-sm font-medium text-green-900 dark:text-green-100 cursor-pointer">
                    Activate PPPoE account immediately upon saving (creates account in Mikrotik)
                  </label>
                </div>
                {!formData.activate_pppoe && formData.pppoe_username && (
                  <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      <strong>Note:</strong> PPPoE credentials will be saved but not activated in Mikrotik. 
                      You can activate later using "Activate Selected" button.
                    </p>
                  </div>
                )}
              </div>
              
              <Button type="submit" className="w-full">Register Subscriber</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>All Subscribers</CardTitle>
              <CardDescription>{filteredSubscribers.length} total subscribers</CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              {selectedSubscribers.length > 0 && (
                <Button 
                  onClick={handleBulkActivate} 
                  disabled={activating}
                  data-testid="bulk-activate-button"
                  className="bg-green-600 hover:bg-green-700 order-2 sm:order-1"
                  size="sm"
                >
                  {activating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Activate ({selectedSubscribers.length})
                </Button>
              )}
              <div className="relative w-full sm:w-64 order-1 sm:order-2">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 h-9"
                  data-testid="search-input"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="space-y-2 sm:hidden">
                {paginatedSubscribers.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No subscribers found
                  </div>
                ) : (
                  paginatedSubscribers.map((sub) => (
                    <div key={sub.account_number} className="p-3 border rounded-lg bg-card">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedSubscribers.includes(sub.account_number)}
                          onChange={() => handleSelectSubscriber(sub.account_number)}
                          className="w-4 h-4 mt-1 text-primary bg-white border-gray-300 rounded focus:ring-primary shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-mono text-xs text-muted-foreground">{sub.account_number}</p>
                              <p className="font-medium text-sm truncate">{sub.first_name} {sub.last_name}</p>
                              <p className="text-xs text-muted-foreground">{sub.plan_id || sub.plan_name || '-'}</p>
                            </div>
                            <Badge variant={sub.is_active ? "default" : "secondary"} className={sub.is_active ? "bg-green-600 shrink-0" : "shrink-0"}>
                              {sub.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t">
                            <div className="flex items-center gap-2">
                              {sub.pppoe_activated && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-50 text-green-700 border-green-200">PPPoE</Badge>
                              )}
                              {(sub.wallet_balance || 0) > 0 && (
                                <span className="text-xs text-green-600">₱{(sub.wallet_balance || 0).toLocaleString()}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleViewHistory(sub)}
                              >
                                <User className="h-4 w-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewHistory(sub)}>
                                    <User className="mr-2 h-4 w-4" />
                                    View Records
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openEditDialog(sub)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Details
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openChangePlanDialog(sub)}>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Change Plan
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => openChargeDialog(sub)}>
                                    <DollarSign className="mr-2 h-4 w-4" />
                                    Add Charge
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {sub.is_active ? (
                                    <DropdownMenuItem onClick={() => openDeactivateDialog(sub)} className="text-amber-600">
                                      <PowerOff className="mr-2 h-4 w-4" />
                                      Deactivate
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => openReactivateDialog(sub)} className="text-green-600">
                                      <Power className="mr-2 h-4 w-4" />
                                      Reactivate
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => openDeleteDialog(sub)} className="text-red-600">
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Desktop View - Table */}
              <div className="rounded-md border overflow-x-auto hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10 px-2">
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.length === filteredSubscribers.length && filteredSubscribers.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                      />
                    </TableHead>
                    <TableHead className="min-w-[180px]">
                      <span className="hidden sm:inline">Account / Name</span>
                      <span className="sm:hidden">Subscriber</span>
                    </TableHead>
                    <TableHead className="hidden md:table-cell">Contact</TableHead>
                    <TableHead className="hidden lg:table-cell">Plan</TableHead>
                    <TableHead className="hidden xl:table-cell">Installation</TableHead>
                    <TableHead className="hidden xl:table-cell">Wallet</TableHead>
                    <TableHead className="min-w-[80px]">Status</TableHead>
                    <TableHead className="text-right min-w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedSubscribers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No subscribers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedSubscribers.map((sub) => (
                      <TableRow key={sub.account_number}>
                        <TableCell className="px-2">
                          <input
                            type="checkbox"
                            checked={selectedSubscribers.includes(sub.account_number)}
                            onChange={() => handleSelectSubscriber(sub.account_number)}
                            className="w-4 h-4 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                          />
                        </TableCell>
                        {/* Account + Name + Status (combined for mobile) */}
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-mono text-xs text-muted-foreground">{sub.account_number}</div>
                            <div className="font-medium text-sm">{sub.first_name} {sub.last_name}</div>
                            {/* Show contact info on mobile */}
                            <div className="md:hidden text-xs text-muted-foreground">{sub.phone || '-'}</div>
                            {/* Show plan on mobile/tablet */}
                            <div className="lg:hidden text-xs text-muted-foreground">{sub.plan_id || sub.plan_name || '-'}</div>
                            {/* Show PPPoE status on mobile */}
                            <div className="flex items-center gap-1 sm:hidden">
                              {sub.pppoe_activated ? (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-50 text-green-700 border-green-200">PPPoE</Badge>
                              ) : null}
                              {(sub.wallet_balance || 0) > 0 && (
                                <span className="text-[10px] text-green-600">₱{(sub.wallet_balance || 0).toLocaleString()}</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        {/* Contact - hidden on mobile */}
                        <TableCell className="hidden md:table-cell">
                          <div className="text-sm">{sub.phone || '-'}</div>
                        </TableCell>
                        {/* Plan - hidden on mobile/tablet */}
                        <TableCell className="hidden lg:table-cell text-sm">
                          {sub.plan_id || sub.plan_name || '-'}
                        </TableCell>
                        {/* Installation - hidden until xl */}
                        <TableCell className="hidden xl:table-cell text-xs">
                          {sub.installation_date ? new Date(sub.installation_date).toLocaleDateString() : '-'}
                        </TableCell>
                        {/* Wallet - hidden until xl */}
                        <TableCell className="hidden xl:table-cell">
                          {(sub.wallet_balance || 0) > 0 ? (
                            <span className="text-green-600 font-medium text-sm">₱{(sub.wallet_balance || 0).toLocaleString()}</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">₱0</span>
                          )}
                        </TableCell>
                        {/* Status column - combined PPPoE + Active status */}
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge 
                              variant={sub.is_active ? "default" : "secondary"} 
                              className={`text-[10px] sm:text-xs w-fit ${sub.is_active ? "bg-green-600" : "bg-red-100 text-red-700"}`}
                            >
                              {sub.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <div className="hidden sm:block">
                              {sub.pppoe_activated ? (
                                <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                                  PPPoE
                                </Badge>
                              ) : sub.pppoe_username ? (
                                <Badge variant="outline" className="text-[10px] bg-yellow-50 text-yellow-700 border-yellow-200">
                                  Pending
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                        </TableCell>
                        {/* Actions */}
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewHistory(sub)}
                              className="text-blue-600 border-blue-600 hover:bg-blue-50 text-xs px-2 h-8"
                            >
                              <span className="hidden sm:inline">View</span>
                              <User className="h-3.5 w-3.5 sm:hidden" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => openEditDialog(sub)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openChangePlanDialog(sub)}>
                                  <RefreshCw className="mr-2 h-4 w-4" />
                                  Change Plan
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openChargeDialog(sub)}>
                                  <DollarSign className="mr-2 h-4 w-4" />
                                  Add Charge
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePrintSOA(sub)}>
                                  <FileText className="mr-2 h-4 w-4" />
                                  Print SOA
                                </DropdownMenuItem>
                                {sub.pppoe_username && sub.pppoe_password && sub.pppoe_profile && !sub.is_active === false && (
                                  <DropdownMenuItem onClick={() => handleActivateSingle(sub.account_number)}>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Sync to Mikrotik
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {sub.is_active ? (
                                  <DropdownMenuItem onClick={() => openDeactivateDialog(sub)} className="text-amber-600">
                                    <PowerOff className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => openReactivateDialog(sub)} className="text-green-600">
                                    <Power className="mr-2 h-4 w-4" />
                                    Reactivate
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openDeleteDialog(sub)} className="text-red-600">
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
              
              {/* Pagination */}
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredSubscribers.length}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Payment History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle>{selectedSubscriberHistory?.first_name} {selectedSubscriberHistory?.last_name}</DialogTitle>
                <DialogDescription>
                  Account: {selectedSubscriberHistory?.account_number}
                </DialogDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => handlePrintSOA(selectedSubscriberHistory)}
                className="ml-4 shrink-0"
              >
                <FileText className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">Print SOA</span>
                <span className="sm:hidden">SOA</span>
              </Button>
            </div>
          </DialogHeader>

          {/* Subscriber Details Card */}
          <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              <User className="h-4 w-4" />
              Subscriber Details
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Mobile Number</p>
                <p className="font-medium">{selectedSubscriberHistory?.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Plan</p>
                <p className="font-medium">{selectedSubscriberHistory?.plan_id || 'N/A'}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Status</p>
                <Badge variant={selectedSubscriberHistory?.is_active ? "default" : "secondary"} className={selectedSubscriberHistory?.is_active ? "bg-green-600" : ""}>
                  {selectedSubscriberHistory?.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Installation Date</p>
                <p className="font-medium">
                  {selectedSubscriberHistory?.installation_date 
                    ? new Date(selectedSubscriberHistory.installation_date).toLocaleDateString() 
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Billing Day</p>
                <p className="font-medium">
                  {selectedSubscriberHistory?.billing_day 
                    ? `Every ${selectedSubscriberHistory.billing_day}${['st','nd','rd'][((selectedSubscriberHistory.billing_day+90)%100-10)%10-1]||'th'} of the month`
                    : selectedSubscriberHistory?.billing_period || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Grace Period</p>
                <p className="font-medium">
                  {selectedSubscriberHistory?.grace_period_days !== undefined 
                    ? (selectedSubscriberHistory.grace_period_days === 0 
                        ? 'No grace period' 
                        : `${selectedSubscriberHistory.grace_period_days} day${selectedSubscriberHistory.grace_period_days > 1 ? 's' : ''}`)
                    : '5 days (default)'}
                </p>
              </div>
              <div className="md:col-span-1">
                <p className="text-muted-foreground text-xs">Address</p>
                <p className="font-medium text-xs">
                  {[
                    selectedSubscriberHistory?.street,
                    selectedSubscriberHistory?.barangay,
                    selectedSubscriberHistory?.municipality,
                    selectedSubscriberHistory?.province
                  ].filter(Boolean).join(', ') || selectedSubscriberHistory?.address || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Wallet Balance Card */}
          {(selectedSubscriberHistory?.wallet_balance || 0) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-green-700 font-medium">Wallet Credit Available</p>
                  <p className="text-2xl font-bold text-green-600">₱{(selectedSubscriberHistory?.wallet_balance || 0).toLocaleString()}</p>
                </div>
              </div>
              <Badge className="bg-green-600">Active Credit</Badge>
            </div>
          )}

          {/* Tabbed Content */}
          <Tabs defaultValue="invoices" className="w-full">
            <TabsList className="grid w-full grid-cols-3 h-auto">
              <TabsTrigger value="invoices" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 text-xs sm:text-sm" data-testid="tab-invoices">
                <FileText className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Invoices ({invoiceHistory.length})</span>
                <span className="sm:hidden text-center leading-tight">Invoices<br/>({invoiceHistory.length})</span>
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 text-xs sm:text-sm" data-testid="tab-payments">
                <Receipt className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Payments ({paymentHistory.length})</span>
                <span className="sm:hidden text-center leading-tight">Payments<br/>({paymentHistory.length})</span>
              </TabsTrigger>
              <TabsTrigger value="equipment" className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 text-xs sm:text-sm" data-testid="tab-equipment">
                <Package className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline">Equipment & Materials ({assignedEquipment.length})</span>
                <span className="sm:hidden text-center leading-tight">Equipment<br/>({assignedEquipment.length})</span>
              </TabsTrigger>
            </TabsList>

            {/* Invoices Tab */}
            <TabsContent value="invoices" className="mt-4">
              {(() => {
                const totalInvoicePages = Math.ceil(invoiceHistory.length / historyPageSize);
                const paginatedInvoices = invoiceHistory.slice(
                  (invoicePage - 1) * historyPageSize,
                  invoicePage * historyPageSize
                );
                
                return (
                  <>
                    {/* Mobile View - Cards */}
                    <div className="space-y-3 sm:hidden">
                      {paginatedInvoices.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground border rounded-lg">
                          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          No invoices found
                        </div>
                      ) : (
                        paginatedInvoices.map((invoice) => (
                          <div key={invoice.invoice_number} className="p-3 border rounded-lg bg-card shadow-sm">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <p className="font-mono text-xs text-muted-foreground">{invoice.invoice_number}</p>
                              <div className="flex items-center gap-1">
                                {!invoice.paid && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => openAdjustDialog(invoice)}
                                    title="Adjust bill amount"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </Button>
                                )}
                                <Badge variant={invoice.paid ? "default" : "destructive"} className={invoice.paid ? "bg-green-600 shrink-0" : "shrink-0"}>
                                  {invoice.paid ? 'Paid' : 'Unpaid'}
                                </Badge>
                              </div>
                            </div>
                            <p className="text-sm font-medium line-clamp-2" title={invoice.description || invoice.plan_name}>
                              {invoice.description || `${invoice.plan_name || 'Monthly'} Bill`}
                            </p>
                            {invoice.type && (
                              <Badge variant="outline" className="text-xs mt-1.5">
                                {invoice.type}
                              </Badge>
                            )}
                            <div className="flex items-center justify-between mt-3 pt-2 border-t">
                              <span className="text-xs text-muted-foreground">Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                              <span className="font-bold text-base">₱{invoice.amount?.toLocaleString()}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Desktop View - Table */}
                    <div className="rounded-md border hidden sm:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Due Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedInvoices.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                No invoices found
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedInvoices.map((invoice) => (
                              <TableRow key={invoice.invoice_number}>
                                <TableCell className="font-mono text-xs">{invoice.invoice_number}</TableCell>
                                <TableCell className="max-w-[250px]">
                                  <p className="text-sm truncate" title={invoice.description || invoice.plan_name}>
                                    {invoice.description || `${invoice.plan_name || 'Monthly'} Bill`}
                                  </p>
                                  {invoice.type && (
                                    <Badge variant="outline" className="text-xs mt-1">
                                      {invoice.type}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="font-bold">₱{invoice.amount?.toLocaleString()}</TableCell>
                                <TableCell>{new Date(invoice.due_date).toLocaleDateString()}</TableCell>
                                <TableCell>
                                  <Badge variant={invoice.paid ? "default" : "destructive"} className={invoice.paid ? "bg-green-600" : ""}>
                                    {invoice.paid ? 'Paid' : 'Unpaid'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openAdjustDialog(invoice)}
                                    disabled={invoice.paid}
                                    title={invoice.paid ? "Cannot adjust paid invoice" : "Adjust bill amount"}
                                    data-testid={`adjust-invoice-${invoice.invoice_number}`}
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Invoice Pagination */}
                    {invoiceHistory.length > historyPageSize && (
                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                        <span className="text-sm text-muted-foreground">
                          {(invoicePage - 1) * historyPageSize + 1}-{Math.min(invoicePage * historyPageSize, invoiceHistory.length)} of {invoiceHistory.length}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                            disabled={invoicePage === 1}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setInvoicePage(p => Math.min(totalInvoicePages, p + 1))}
                            disabled={invoicePage === totalInvoicePages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments" className="mt-4">
              {(() => {
                const totalPaymentPages = Math.ceil(paymentHistory.length / historyPageSize);
                const paginatedPayments = paymentHistory.slice(
                  (paymentPage - 1) * historyPageSize,
                  paymentPage * historyPageSize
                );
                
                return (
                  <>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="min-w-[140px]">
                              <span className="hidden sm:inline">OR Number / Mode</span>
                              <span className="sm:hidden">OR/Mode</span>
                            </TableHead>
                            <TableHead className="min-w-[120px]">
                              <span className="hidden sm:inline">Description / Amount</span>
                              <span className="sm:hidden">Details</span>
                            </TableHead>
                            <TableHead className="hidden md:table-cell">Date</TableHead>
                            <TableHead className="hidden lg:table-cell">Received By</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {paginatedPayments.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                No payments found
                              </TableCell>
                            </TableRow>
                          ) : (
                            paginatedPayments.map((payment) => (
                              <TableRow key={payment.or_number}>
                                {/* OR Number + Mode (combined) */}
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="font-mono text-xs font-medium">{payment.or_number}</div>
                                    <div className="text-xs text-muted-foreground capitalize flex items-center gap-1">
                                      <CreditCard className="h-3 w-3" />
                                      {payment.mode || 'cash'}
                                    </div>
                                    {/* Show date on mobile */}
                                    <div className="text-xs text-muted-foreground md:hidden">
                                      {formatPHDate(payment.payment_date)}
                                    </div>
                                  </div>
                                </TableCell>
                                {/* Description + Amount (combined) */}
                                <TableCell>
                                  <div className="space-y-1">
                                    <div className="text-xs text-muted-foreground line-clamp-2">
                                      {payment.description || payment.allocation_details?.map(a => a.invoice_number).join(', ') || 'Payment'}
                                    </div>
                                    <div className="font-bold text-green-600">
                                      ₱{(payment.total_amount || payment.amount || 0).toLocaleString()}
                                    </div>
                                    {/* Show received by on mobile */}
                                    <div className="text-xs text-muted-foreground lg:hidden">
                                      by {payment.received_by || 'System'}
                                    </div>
                                  </div>
                                </TableCell>
                                {/* Date - hidden on mobile */}
                                <TableCell className="hidden md:table-cell text-sm">
                                  {formatPHDateTime(payment.payment_date)}
                                </TableCell>
                                {/* Received By - hidden on mobile/tablet */}
                                <TableCell className="hidden lg:table-cell text-sm">
                                  {payment.received_by || 'System'}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Payment Pagination */}
                    {paymentHistory.length > historyPageSize && (
                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                        <span className="text-sm text-muted-foreground">
                          {(paymentPage - 1) * historyPageSize + 1}-{Math.min(paymentPage * historyPageSize, paymentHistory.length)} of {paymentHistory.length}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPaymentPage(p => Math.max(1, p - 1))}
                            disabled={paymentPage === 1}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPaymentPage(p => Math.min(totalPaymentPages, p + 1))}
                            disabled={paymentPage === totalPaymentPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* Equipment & Materials Tab */}
            <TabsContent value="equipment" className="mt-4">
              {assignedEquipment.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg border border-dashed">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3 opacity-50" />
                  <p className="text-muted-foreground">No equipment or materials assigned to this subscriber</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Serialized Equipment */}
                  {assignedEquipment.filter(e => e.item_type === 'equipment' || e.mac_address || e.serial_number || e.unit_id).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                        <Wifi className="h-4 w-4" /> Equipment
                      </p>
                      <div className="space-y-2">
                        {assignedEquipment
                          .filter(e => e.item_type === 'equipment' || e.mac_address || e.serial_number || e.unit_id)
                          .map((equipment, idx) => (
                          <div 
                            key={equipment.unit_id || idx} 
                            className="p-4 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg flex items-center justify-between"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                                <Wifi className="h-6 w-6 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-purple-900 dark:text-purple-100 text-lg">
                                  {equipment.item_name || equipment.item_code}
                                </p>
                                <div className="flex items-center gap-4 text-sm text-purple-600 dark:text-purple-400 mt-1">
                                  {equipment.mac_address && <span>MAC: <span className="font-mono">{equipment.mac_address}</span></span>}
                                  {equipment.mac_address && equipment.serial_number && <span className="text-purple-300">|</span>}
                                  {equipment.serial_number && <span>S/N: <span className="font-mono">{equipment.serial_number}</span></span>}
                                </div>
                                {equipment.assigned_via === 'job_order' && equipment.job_order_id && (
                                  <p className="text-xs text-purple-500 mt-1">Job Order: {equipment.job_order_id}</p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-purple-600">
                                {equipment.assigned_via === 'registration' ? 'Registration' : 'Job Order'}
                              </Badge>
                              {equipment.assigned_date && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  {new Date(equipment.assigned_date).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Non-Serialized Materials */}
                  {assignedEquipment.filter(e => e.item_type === 'material').length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                        <Package className="h-4 w-4" /> Materials Used
                      </p>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Quantity</TableHead>
                              <TableHead>Job Order</TableHead>
                              <TableHead>Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {assignedEquipment
                              .filter(e => e.item_type === 'material')
                              .map((material, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="font-medium">{material.item_name || material.item_code}</TableCell>
                                <TableCell>{material.quantity} {material.unit}</TableCell>
                                <TableCell>
                                  <div>
                                    <span className="font-mono text-xs">{material.job_order_id}</span>
                                    {material.job_order_type && (
                                      <p className="text-xs text-muted-foreground">{material.job_order_type}</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-xs">
                                  {material.assigned_date ? new Date(material.assigned_date).toLocaleDateString() : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Change Plan Dialog */}
      <Dialog open={changePlanDialogOpen} onOpenChange={setChangePlanDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Subscription Plan</DialogTitle>
            <DialogDescription>
              {selectedSubscriber && `Update plan for ${selectedSubscriber.first_name} ${selectedSubscriber.last_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Current Plan</Label>
              <p className="text-sm text-muted-foreground">{selectedSubscriber?.plan_id || 'None'}</p>
            </div>
            <div>
              <Label>New Plan</Label>
              <Select value={changePlanForm.new_plan_id} onValueChange={(v) => setChangePlanForm({...changePlanForm, new_plan_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.name} value={plan.name}>
                      {plan.name} - ₱{plan.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>New PPPoE Profile (Mikrotik)</Label>
              <Select value={changePlanForm.new_pppoe_profile} onValueChange={(v) => setChangePlanForm({...changePlanForm, new_pppoe_profile: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select profile" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((profile) => (
                    <SelectItem key={profile} value={profile}>{profile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="generate_prorated_change"
                checked={changePlanForm.generate_prorated_bill}
                onCheckedChange={(c) => setChangePlanForm({...changePlanForm, generate_prorated_bill: c})}
              />
              <Label htmlFor="generate_prorated_change" className="text-sm">Generate prorated adjustment bill</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePlanDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleChangePlan} disabled={actionLoading || !changePlanForm.new_plan_id}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Dialog */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-600">Deactivate Subscriber</DialogTitle>
            <DialogDescription>
              {selectedSubscriber && `Deactivate ${selectedSubscriber.first_name} ${selectedSubscriber.last_name}'s account`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Disconnection Profile (No Internet)</Label>
              <Select value={deactivateForm.disconnection_profile} onValueChange={(v) => setDeactivateForm({...deactivateForm, disconnection_profile: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((profile) => (
                    <SelectItem key={profile} value={profile}>{profile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Usually "NON-PAYMENTS" or a profile without bandwidth</p>
            </div>
            <div>
              <Label>Reason</Label>
              <Input 
                value={deactivateForm.reason} 
                onChange={(e) => setDeactivateForm({...deactivateForm, reason: e.target.value})}
                placeholder="e.g., Non-payment, Customer request"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="generate_final_bill"
                checked={deactivateForm.generate_final_bill}
                onCheckedChange={(c) => setDeactivateForm({...deactivateForm, generate_final_bill: c})}
              />
              <Label htmlFor="generate_final_bill" className="text-sm">Generate final bill (prorated to disconnection date)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeactivate} disabled={actionLoading}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Dialog */}
      <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-600">Reactivate Subscriber</DialogTitle>
            <DialogDescription>
              {selectedSubscriber && `Reactivate ${selectedSubscriber.first_name} ${selectedSubscriber.last_name}'s account`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subscription Plan</Label>
              <Select value={reactivateForm.plan_id} onValueChange={(v) => setReactivateForm({...reactivateForm, plan_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.name} value={plan.name}>
                      {plan.name} - ₱{plan.price}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>PPPoE Profile</Label>
              <Select value={reactivateForm.pppoe_profile} onValueChange={(v) => setReactivateForm({...reactivateForm, pppoe_profile: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select active profile" />
                </SelectTrigger>
                <SelectContent>
                  {profiles.map((profile) => (
                    <SelectItem key={profile} value={profile}>{profile}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="generate_prorated_reactivate"
                checked={reactivateForm.generate_prorated_bill}
                onCheckedChange={(c) => setReactivateForm({...reactivateForm, generate_prorated_bill: c})}
              />
              <Label htmlFor="generate_prorated_reactivate" className="text-sm">Generate prorated bill (from today to billing date)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReactivate} disabled={actionLoading || !reactivateForm.pppoe_profile} className="bg-green-600 hover:bg-green-700">
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Reactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Subscriber</DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. The PPPoE account will also be removed from Mikrotik.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> You are about to delete <strong>{selectedSubscriber?.first_name} {selectedSubscriber?.last_name}</strong> ({selectedSubscriber?.account_number}).
              </p>
            </div>
            <div>
              <Label>Enter Admin Password to Confirm</Label>
              <Input 
                type="password"
                value={deleteForm.admin_password} 
                onChange={(e) => setDeleteForm({...deleteForm, admin_password: e.target.value})}
                placeholder="Your admin password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={actionLoading || !deleteForm.admin_password}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Charge Dialog */}
      <Dialog open={chargeDialogOpen} onOpenChange={setChargeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Manual Charge</DialogTitle>
            <DialogDescription>
              {selectedSubscriber && `Add charge for ${selectedSubscriber.first_name} ${selectedSubscriber.last_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Charge Type</Label>
              <Select value={chargeForm.charge_type} onValueChange={(v) => setChargeForm({...chargeForm, charge_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Equipment">Equipment</SelectItem>
                  <SelectItem value="Service Fee">Service Fee</SelectItem>
                  <SelectItem value="Reconnection Fee">Reconnection Fee</SelectItem>
                  <SelectItem value="Installation">Installation</SelectItem>
                  <SelectItem value="Relocation">Relocation</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Input 
                value={chargeForm.description} 
                onChange={(e) => setChargeForm({...chargeForm, description: e.target.value})}
                placeholder="e.g., Router replacement, Cable repair"
              />
            </div>
            <div>
              <Label>Amount (₱)</Label>
              <Input 
                type="number"
                value={chargeForm.amount} 
                onChange={(e) => setChargeForm({...chargeForm, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChargeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCharge} disabled={actionLoading || !chargeForm.description || !chargeForm.amount}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Charge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Adjustment Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust Invoice Amount</DialogTitle>
            <DialogDescription>
              {adjustingInvoice && `Adjusting invoice ${adjustingInvoice.invoice_number}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {adjustingInvoice && (
              <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Current Amount:</span>
                  <span className="font-bold">₱{adjustingInvoice.amount?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Description:</span>
                  <span className="truncate max-w-[200px]" title={adjustingInvoice.description}>
                    {adjustingInvoice.description || adjustingInvoice.plan_name || 'Invoice'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Due Date:</span>
                  <span>{adjustingInvoice.due_date && new Date(adjustingInvoice.due_date).toLocaleDateString()}</span>
                </div>
              </div>
            )}
            <div>
              <Label>New Amount (₱) *</Label>
              <Input 
                type="number"
                step="0.01"
                min="0"
                value={adjustForm.new_amount} 
                onChange={(e) => setAdjustForm({...adjustForm, new_amount: e.target.value})}
                placeholder="Enter new amount"
                data-testid="adjust-amount-input"
              />
            </div>
            <div>
              <Label>Remark / Reason *</Label>
              <Textarea 
                value={adjustForm.remark} 
                onChange={(e) => setAdjustForm({...adjustForm, remark: e.target.value})}
                placeholder="Enter reason for adjustment (e.g., Promotional discount, Billing error correction)"
                rows={3}
                data-testid="adjust-remark-input"
              />
              <p className="text-xs text-muted-foreground mt-1">This will be logged for audit trail</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleAdjustInvoice} 
              disabled={adjustLoading || !adjustForm.new_amount || !adjustForm.remark.trim()}
              data-testid="confirm-adjust-btn"
            >
              {adjustLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adjust Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subscriber Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Subscriber Details</DialogTitle>
            <DialogDescription>
              {selectedSubscriber && `Update information for ${selectedSubscriber.account_number}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input 
                  value={editForm.first_name} 
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input 
                  value={editForm.last_name} 
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                />
              </div>
            </div>
            
            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editForm.email} 
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={editForm.phone} 
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
              <Label className="font-semibold">Address</Label>
              
              <div>
                <Label className="text-xs text-muted-foreground">Street Address</Label>
                <Input 
                  value={editForm.street} 
                  onChange={(e) => setEditForm({...editForm, street: e.target.value})}
                  placeholder="House/Unit No., Street Name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Province</Label>
                  <Select value={editForm.province} onValueChange={handleEditProvinceChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Province" />
                    </SelectTrigger>
                    <SelectContent>
                      {editProvinces.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Municipality</Label>
                  <Select 
                    value={editForm.municipality} 
                    onValueChange={handleEditMunicipalityChange}
                    disabled={!editForm.province}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Municipality" />
                    </SelectTrigger>
                    <SelectContent>
                      {editMunicipalities.map((muni) => (
                        <SelectItem key={muni} value={muni}>{muni}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Barangay</Label>
                <Select 
                  value={editForm.barangay} 
                  onValueChange={(v) => setEditForm({...editForm, barangay: v})}
                  disabled={!editForm.municipality}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Barangay" />
                  </SelectTrigger>
                  <SelectContent>
                    {editBarangays.map((brgy) => (
                      <SelectItem key={brgy} value={brgy}>{brgy}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Modem MAC Address */}
            <div>
              <Label>Modem MAC Address</Label>
              <Input 
                value={editForm.modem_mac} 
                onChange={(e) => setEditForm({...editForm, modem_mac: e.target.value})}
                placeholder="XX:XX:XX:XX:XX:XX"
              />
            </div>
            
            {/* Billing Settings */}
            <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <div>
                <Label>Billing Day</Label>
                <Select 
                  value={editForm.billing_day?.toString() || '30'} 
                  onValueChange={(value) => setEditForm({...editForm, billing_day: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Grace Period</Label>
                <Select 
                  value={editForm.grace_period_days?.toString() || '5'} 
                  onValueChange={(value) => setEditForm({...editForm, grace_period_days: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px]">
                    {Array.from({ length: 31 }, (_, i) => i).map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day === 0 ? 'None' : `${day} day${day > 1 ? 's' : ''}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Mikrotik Selection */}
            {mikrotikRouters.length > 0 && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Target Mikrotik Router(s)
                  </Label>
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditForm({...editForm, mikrotik_ids: mikrotikRouters.map(r => r.router_id)})}
                      className="text-xs h-7"
                    >
                      All
                    </Button>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditForm({...editForm, mikrotik_ids: []})}
                      className="text-xs h-7"
                    >
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {mikrotikRouters.map((router) => (
                    <div 
                      key={router.router_id}
                      className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer transition-colors ${
                        editForm.mikrotik_ids.includes(router.router_id) 
                          ? 'bg-primary/10 border-primary' 
                          : 'bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                      onClick={() => {
                        setEditForm(prev => ({
                          ...prev,
                          mikrotik_ids: prev.mikrotik_ids.includes(router.router_id)
                            ? prev.mikrotik_ids.filter(id => id !== router.router_id)
                            : [...prev.mikrotik_ids, router.router_id]
                        }));
                      }}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        editForm.mikrotik_ids.includes(router.router_id)
                          ? 'bg-primary border-primary text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {editForm.mikrotik_ids.includes(router.router_id) && (
                          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                            <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{router.name}</p>
                        <p className="text-xs text-muted-foreground">{router.ip_address}:{router.port || 8728}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateSubscriber} disabled={actionLoading || !editForm.first_name || !editForm.last_name}>
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
